import type { DataSession } from "../../../data/core/session";
import type { z } from "zod";
import { NoAuthError } from "@utils/NoAuthError";
import { RouterOutputs, trpc } from "@utils/trpc";
import { getQueryKey } from "@trpc/react-query";
import { unlockedPrivateSongsWireSchema } from "@libs/wire";

export type UnlockedPrivateSongsQueryData =
  RouterOutputs["user"]["accesses"]["listUnlockedPrivateSongs"];

/**
 * Compile-time bidirectional drift check: `z.infer<typeof
 * unlockedPrivateSongsWireSchema>` and the router output must be mutually
 * assignable. Forward catches "schema is missing fields"; backward catches
 * "schema is narrower than router output" (which would reject valid server
 * data and pin the cache to stale rows). See `useLibrary/queryFunctions.ts`
 * for the rationale.
 */
type _UnlockedPrivateSongsWireSchemaSubset =
  z.infer<
    typeof unlockedPrivateSongsWireSchema
  > extends UnlockedPrivateSongsQueryData
    ? true
    : never;
type _UnlockedPrivateSongsWireSchemaSuperset =
  UnlockedPrivateSongsQueryData extends z.infer<
    typeof unlockedPrivateSongsWireSchema
  >
    ? true
    : never;
const _unlockedPrivateSongsWireSchemaDriftCheck: [
  _UnlockedPrivateSongsWireSchemaSubset,
  _UnlockedPrivateSongsWireSchemaSuperset,
] = [true, true];
void _unlockedPrivateSongsWireSchemaDriftCheck;

export const unlockedPrivateSongsQueryKey = () =>
  getQueryKey(trpc.user.accesses.listUnlockedPrivateSongs);

/**
 * Back-compat alias matching the old top-level constant name. Several
 * call sites (e.g. `PaymentsCallbackHandler`) reach for this to invalidate
 * after a successful unlock; we keep the name to avoid a typecheck cascade.
 */
export const USER_LISTUNLOCKEDPRIVATESONGS_QUERY_KEY =
  unlockedPrivateSongsQueryKey();

export const unlockedPrivateSongsQuery = (data: DataSession) =>
  data.cache.durableQueryOptions({
    queryKey: unlockedPrivateSongsQueryKey(),
    queryFn: () => {
      if (!data.auth.currentUser()) {
        throw new NoAuthError("unlockedPrivateSongsQuery queryFn");
      }
      return data.api.user.accesses.listUnlockedPrivateSongs.query();
    },
    schema: unlockedPrivateSongsWireSchema,
  });
